import { ChangeDetectionStrategy, ChangeDetectorRef, Component } from '@angular/core';
import { FormControl } from '@angular/forms';
import { GridOptions, ICellRendererParams } from 'ag-grid-community';
import { isEmpty } from 'lodash';
import { combineLatest, merge, Observable, of, ReplaySubject } from 'rxjs';
import { debounceTime, distinctUntilChanged, map, tap } from 'rxjs/operators';
import { CovanceGridColumn, FilterHeaderComponent, FilterHeaderParams } from '../covance-ag-grid';
import { SearchParams, UserEmulationsReadModel } from './user-emulation';
import { UserEmulationGroupRenderer } from './user-emulation-group-renderer.component';
import { UserEmulationStorage } from './user-emulation-storage';
import { UserInfo } from './user-info';

interface UserEmulationGridRowItem extends ICellRendererParams {
    data: UserInfo;
}

@Component({
    selector: 'user-emulation',
    template: require('./user-emulation.component.html'),
    styles: [require('./user-emulation.component.scss')],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class UserEmulationComponent {
    public results$: Observable<UserInfo[]> ;

    public firstNameSearchTerm = new FormControl('');
    public lastNameSearchTerm = new FormControl('');
    public userNameSearchTerm = new FormControl('');
    public protocolSearchTerm = new FormControl('');
    public projectSearchTerm = new FormControl('');
    public siteNumSearchTerm = new FormControl('');

    public sponsorFilterSearchTerm = new FormControl('');
    public protocolFilterSearchTerm = new FormControl('');
    public projectNumberFilterSearchTerm = new FormControl('');
    public regionFilterSearchTerm = new FormControl('');
    public subProtocolFilterSearchTerm = new FormControl('');
    public siteNumFilterSearchTerm = new FormControl('');
    public investigatorFilterSearchTerm = new FormControl('');
    public applicationRoleFilterSearchTerm = new FormControl('');
    public blindingRoleFilterSearchTerm = new FormControl('');

    public currentPage: number;
    public totalPages: number;

    public columns: CovanceGridColumn[] = [
        {
            hide: true,
            headerName: 'User',
            field: 'userId',
            rowGroupIndex: 0,
            valueGetter: (params: UserEmulationGridRowItem) => {
                if (!params.data) {
                    return null;
                }

                const data: string[] = [];
                const user = params.data;

                if (!isEmpty(user.fullName)) {
                    data.push(user.fullName);
                }

                data.push(`(${user.userName})`);

                return data.join(' ');
            }
        },
        {
            headerName: 'Sponsor',
            field: 'sponsor',
            headerComponentFramework: FilterHeaderComponent,
            headerComponentParams: { searchTerm: this.sponsorFilterSearchTerm } as FilterHeaderParams
        },
        {
            headerName: 'Protocol',
            field: 'clientProtocol',
            headerComponentFramework: FilterHeaderComponent,
            headerComponentParams: { searchTerm: this.protocolFilterSearchTerm } as FilterHeaderParams
        },
        {
            headerName: 'Project No.',
            field: 'covanceProjectNumber',
            headerComponentFramework: FilterHeaderComponent,
            headerComponentParams: { searchTerm: this.projectNumberFilterSearchTerm } as FilterHeaderParams
        },
        {
            headerName: 'Region',
            field: 'region',
            headerComponentFramework: FilterHeaderComponent,
            headerComponentParams: { searchTerm: this.regionFilterSearchTerm } as FilterHeaderParams
        },
        {
            headerName: 'Site No.',
            field: 'siteNum',
            headerComponentFramework: FilterHeaderComponent,
            headerComponentParams: { searchTerm: this.siteNumFilterSearchTerm } as FilterHeaderParams
        },
        {
            headerName: 'Investigator',
            field: 'investigatorFullName',
            headerComponentFramework: FilterHeaderComponent,
            headerComponentParams: { searchTerm: this.investigatorFilterSearchTerm } as FilterHeaderParams
        },
        {
            headerName: 'Application Role',
            field: 'applicationRole',
            headerComponentFramework: FilterHeaderComponent,
            headerComponentParams: { searchTerm: this.applicationRoleFilterSearchTerm } as FilterHeaderParams
        },
        {
            headerName: 'Blinding Role',
            field: 'blindingRole',
            headerComponentFramework: FilterHeaderComponent,
            headerComponentParams: { searchTerm: this.blindingRoleFilterSearchTerm } as FilterHeaderParams
        },
    ];

    public gridOptions: GridOptions = {
        groupDefaultExpanded: 0,
        groupUseEntireRow: true,      
        groupRowInnerRendererFramework: UserEmulationGroupRenderer
    };

    private page$ = new ReplaySubject<number>();

    public constructor(private changeDetector: ChangeDetectorRef, private storage: UserEmulationStorage) {
        const searchParams$ = combineLatest(
            this.getFlow(this.firstNameSearchTerm),
            this.getFlow(this.lastNameSearchTerm),
            this.getFlow(this.userNameSearchTerm),
            this.getFlow(this.protocolSearchTerm),
            this.getFlow(this.projectSearchTerm),
            this.getFlow(this.siteNumSearchTerm),
            this.getFlow(this.sponsorFilterSearchTerm),
            this.getFlow(this.protocolFilterSearchTerm),
            this.getFlow(this.projectNumberFilterSearchTerm),
            this.getFlow(this.regionFilterSearchTerm),
            this.getFlow(this.siteNumFilterSearchTerm),
            this.getFlow(this.investigatorFilterSearchTerm),
            this.getFlow(this.applicationRoleFilterSearchTerm),
            this.getFlow(this.blindingRoleFilterSearchTerm)
        ).pipe(
            tap(() => this.showLoadingOverlay()),
            map(([firstName, lastName, userName, protocol, projectNumber, siteNumber, sponsorFilter, protocolFilter, projectNumberFilter, regionFilter, siteNumFilter, investigatorFilter, applicationRoleFilter, blindingRoleFilter]: [string, string, string, string, string, string, string, string, string, string, string, string, string, string]) => {
                return {
                    firstName: firstName,
                    lastName: lastName,
                    userName: userName,
                    protocol: protocol,
                    covanceProjectNumber: projectNumber,
                    siteNumber: siteNumber,
                    sponsorFilter: sponsorFilter,
                    clientProtocolFilter: protocolFilter,
                    covanceProjectNumberFilter: projectNumberFilter,
                    regionFilter: regionFilter,
                    siteNumFilter: siteNumFilter,
                    investigatorFilter: investigatorFilter,
                    applicationRoleFilter: applicationRoleFilter,
                    blindingRoleFilter: blindingRoleFilter
                } as SearchParams;
            })
        );

        this.page$.next(1);

        this.results$ = this.storage.loadUsers(searchParams$, this.page$.asObservable())
            .pipe(
                tap((model: UserEmulationsReadModel) => {
                    this.currentPage = model.currentPage;
                    this.totalPages = model.totalPages; 
                }),
                map((model: UserEmulationsReadModel) => model.users),
                tap(() => this.changeDetector.markForCheck())
            );
    }

    public onFirstPageClicked() {
        this.setPage(1);
    }

    public onPreviousPageClicked() {
        this.setPage(this.currentPage - 1);
    }

    public onNextPagClicked() {
        this.setPage(this.currentPage + 1);
    }

    public onLastPageClicked() {
        this.setPage(this.totalPages);
    }

    private setPage(pageNum: number) {
        this.currentPage = pageNum;
        this.page$.next(pageNum);
        this.showLoadingOverlay();
    }

    private showLoadingOverlay() {
        if (this.gridOptions && this.gridOptions.api) {
            this.gridOptions.api.showLoadingOverlay();
        }
    }

    private getFlow(term: FormControl) {
        return merge(
            of(''),
            term.valueChanges
                .pipe(
                    debounceTime(300),
                    distinctUntilChanged()
                )
        );
    }
}
